import { api } from "@monolith/helpers";

const state = {
  reports: [],
  companyName: "",
  siteSettings: {},
  localSettings: [],
  siteMap: [],
  channels: [],
  socket: false,
  wsListenersUp: false,
  siteMapLoaded: false,
  posDataLoaded: false,
  stripeDataLoaded: false,
  cogsDataLoaded: false,
  sites: [],
  websites: {},
  reportSite: "all",
  cogsSuppliers: [],
  cogsProducts: [],
  cogsUOM: [],
  cogsBaseRecipes: [],
  cogsMenuRecipes: [],
  cogsRecipeCategories: [],
  POSProducts: [],
  POSCategories: [],
  modifiers: [],
  cogsEnabled: false,
  trainingEnabled: false,
  companySettings: {},
  timezone: "",
  gst: "",
  currency: "",
  loading: false,
  region: [],
  regions: [],
  categories: [],
  stripeCustomerDetails: {},
  beamyProducts: [],
  subscribedProducts: [],
  socketConnected: false,
  stripeUpcomingInvoice: {},
  cogsSelectedSite: "",
};

const getters = {
  getCogsSelectedSite: (state) => state.cogsSelectedSite,
  companyGST: () => 1.15,
  getHours: (state) => state.siteSettings.hours,
  stripeUpcomingInvoice: (state) => state.stripeUpcomingInvoice,
  getModifierSets: (state) => (site) => {
    return state.modifiers[site];
  },
  getSubscribedProducts: (state) => state.subscribedProducts,
  stripeCustomerDetails: (state) => state.stripeCustomerDetails,
  beamyProducts: (state) => state.beamyProducts,
  region: (state) => state.region,
  regions: (state) => state.regions,
  loading: (state) => state.loading,
  timezone: (state) => state.timezone,
  companySettings: (state) => state.companySettings,
  trainingEnabled: (state) => state.trainingEnabled,
  cogsEnabled: (state) => state.cogsEnabled,
  reports: (state) => state.reports,
  companyName: (state) => state.companyName,
  reportSite: (state) => state.reportSite,
  siteSettings: (state) => state.siteSettings,
  localSettings: (state) => state.localSettings,
  channels: (state) => state.channels,
  socket: (state) => state.socket,
  wsListenersUp: (state) => state.wsListenersUp,
  siteMap: (state) => state.siteMap,
  siteMapLoaded: (state) => state.siteMapLoaded,
  posDataLoaded: (state) => state.posDataLoaded,
  stripeDataLoaded: (state) => state.stripeDataLoaded,
  cogsDataLoaded: (state) => state.cogsDataLoaded,
  categories: (state) => state.categories,
  sites: (state) => state.sites,
  websites: (state) => state.websites,
  cogsSuppliers: (state) => state.cogsSuppliers,
  cogsProducts: (state) => state.cogsProducts,
  cogsUOM: (state) => state.cogsUOM,
  socketConnected: (state) => state.socketConnected,
  POSProducts: (state) => (site) => {
    return state.POSProducts[site];
  },
  POSProductLookup: (state) => (lookup) => {
    let siteProducts = state.POSProducts[lookup.site];
    if (!siteProducts) {
      return null;
    }
    return Object.values(siteProducts).find((p) => p.key === "product:" + lookup.prod);
  },
  POSCategories: (state) => (site) => {
    return state.POSCategories[site];
  },
  cogsSearchProducts: (state) => (search) => {
    return state.cogsProducts.filter((product) => {
      return product.value.escaped.toLowerCase().includes(search.toLowerCase());
    });
  },
  cogsSearchRecipes: (state) => (search) => {
    return state.cogsBaseRecipes.filter((recipe) => {
      return recipe.value.escaped.toLowerCase().includes(search.toLowerCase());
    });
  },
  cogsBaseRecipes: (state) => state.cogsBaseRecipes,
  cogsMenuRecipes: (state) => state.cogsMenuRecipes,
  cogsRecipeCategories: (state) => state.cogsRecipeCategories,
  findIngredientByID: (state) => (id) => {
    return state.cogsProducts.find((product) => product.id === id);
  },
  findRecipeByID: (state) => (id) => {
    return state.cogsBaseRecipes.find((recipe) => recipe.id === id);
  },
  findRecipeByProductID: (state) => (id) => {
    let base = state.cogsBaseRecipes.filter((recipe) => {
      if (recipe.value && recipe.value.ingredients) {
        return recipe.value.ingredients.find((ingredient) => ingredient.id === id) != null;
      }
    });
    let menu = state.cogsMenuRecipes.filter((recipe) => {
      if (recipe.value && recipe.value.ingredients) {
        return recipe.value.ingredients.find((ingredient) => ingredient.id === id) != null;
      }
    });
    return base.concat(menu);
  },
  cogsCostData: (state) => {
    let data = [];
    let labels = [];
    const costs = state.cogsMenuRecipes.filter((recipe) => recipe.value.cost);
    costs.map((cost) => {
      data.push(cost.value.cost);
      labels.push(cost.value.escaped);
    });
    return [data, labels];
  },
  cogsPercentageData: (state) => {
    let data = [];
    let labels = [];
    const costs = state.cogsMenuRecipes.filter((recipe) => recipe.value.percentage);
    costs.map((cost) => {
      data.push(cost.value.percentage);
      labels.push(cost.value.escaped);
    });
    return [data, labels];
  },
  settingsViaSite: (state) => (site) => {
    return Object.entries(state.siteSettings[site]);
  },
  webSettingsViaSite: (state) => (site) => {
    return state.siteSettings[site];
  },
  getDefaultSite: (state) => {
    if (state.siteMap.length === 0) {
      return [];
    }
    return Array.isArray(state.siteMap) && state.siteMap.length > 1 ? "all" : state.siteMap[0].site;
  },
  getLogoViaSite: (state) => (site) => {
    try {
      return state.siteSettings[site].logo.logo;
    } catch (error) {
      return null;
    }
  },
};

// actions
const actions = {
  updateCogsSelectedSite({ commit }, site) {
    commit("cogsSelectedSiteMutation", site);
  },

  updateModifierSet({ commit }, doc) {
    commit("setModifiersMutation", doc);
  },
  // async getCategories({  }) {
  //   // const categories = await api.getUrl("/api/categories/get", "?include_docs=false", true);
  //   // commit("initialiseCategoriesMutation", categories.rows);
  // },

  async getModifierSets({ commit }) {
    const sites = JSON.parse(localStorage.getItem("sites"));
    await Promise.all(sites.map(async (site) => {
      const result = await api.getUrl("/api/modifiersets/get/" + state.companyName + "/" + site, "?include_docs=false");
      commit("updateModifiersMutation", { site: site, doc: result.rows });
    }));
  },

  updateProduct({ commit }, doc) {
    commit("updateProductMutation", doc);
  },

  updateCategory({ commit }, doc) {
    commit("updateCategoryMutation", doc);
  },

  async setSocketConnected({ commit }, socketConnected) {
    commit("setSocketConnectedMutation", socketConnected);
  },

  updateCompanySettings({ commit }, siteDoc) {
    commit("updateCompanySettingsMutation", siteDoc);
  },

  async setSubscribedProducts({ commit, state }) {
    const currentCounts = {
      sites: state.siteMap.length,
      pos: 0,
      kds: 0,
      cogs: state.cogsEnabled ? 1 : 0,
    };
    state.siteMap.forEach((site) => {
      if (site.stations) {
        currentCounts.pos += site.stations.pos.length;
        currentCounts.kds += site.stations.kds.length;
      }
    });

    const typeToCount = {
      pos: currentCounts.pos,
      kds: currentCounts.kds,
      site: currentCounts.sites,
      cogs: currentCounts.cogs,
    };

    const subscriptionItems =
      state.stripeCustomerDetails &&
      state.stripeCustomerDetails.subscriptions &&
      state.stripeCustomerDetails.subscriptions.data &&
      state.stripeCustomerDetails.subscriptions.data[0] &&
      state.stripeCustomerDetails.subscriptions.data[0].items
        ? state.stripeCustomerDetails.subscriptions.data[0].items.data
        : [];

    const subscriptionItemsLookup = {};
    subscriptionItems.forEach((item) => {
      subscriptionItemsLookup[item.price.id] = {
        subscription_item_id: item.id,
      };
    });
    let subProds = state.beamyProducts.products
      .filter((p) => p.metadata && p.metadata.type && typeToCount[p.metadata.type] > 0)
      .map((p) => {
        const existingItem = subscriptionItemsLookup[p.price_id];

        return {
          product_id: p.id,
          price_id: p.price_id,
          subscription_item_id: existingItem ? existingItem.subscription_item_id : null,
          quantity: typeToCount[p.metadata.type],
          type: p.metadata.type,
          amount: p.unit_amount,
          name: p.name,
          billingCycle: p.recurring.interval,
        };
      });
    commit("setSubscribedProductsMutation", subProds);
  },

  async updateSubscription({ state, dispatch }) {
    let result = await api.update_stripe_subscription(state.subscribedProducts, state.stripeCustomerDetails.subscriptions.data[0].id);
    dispatch("getUpcomingStripeInvoice", result);
  },

  async getUpcomingStripeInvoice({ commit, state }) {
    const details = state.stripeCustomerDetails;
    if (!details || !details.subscriptions || !details.subscriptions.data || !details.subscriptions.data[0]) return;
    let custDetails = {
      customer_id: details.id,
      subscription_id: details.subscriptions.data[0].id,
    };
    let invoice = await api.get_stripe_upcoming_invoice(custDetails);
    commit("setStripeUpcomingInvoiceMutation", invoice);
  },

  async setBeamyProducts({ commit }) {
    let products = await api.get_stripe_products();
    commit("beamyProductsMutation", products);
  },

  async setStripeCustomerDetails({ commit }, details) {
    commit("stripeCustomerDetailsMutation", details);
  },

  async getStripeCustomerDetails({ commit, dispatch }) {
    let details = await api.get_stripe_customer_subscription();
    if (details != null) {
      commit("stripeCustomerDetailsMutation", details);
    } else return;
  },

  setKeyValue({ commit }, key, value, site) {
    commit("setKeyValueMutation", key, value, site);
  },

  setRegion({ commit }, r) {
    commit("regionMutation", r);
  },

  async setRegions({ commit }) {
    const holidays2026 = await fetch("/holidays2026.json");
    const holidays = await holidays2026.json();
    let region = holidays.map((holiday) => {
      if (holiday.Type == "Regional") {
        let r = holiday.HolidayName.split(" ").splice(0, holiday.HolidayName.split(" ").length - 2);
        return r.join(" ");
      }
    });
    region = region.filter((r) => r !== undefined);
    commit("regionsMutation", region);
  },

  setOpeningHours({ commit }, hours) {
    commit("openingHoursMutation", hours);
  },

  companySettings({ commit }, companySettings) {
    commit("companySettingMutation", companySettings);
  },

  setSites({ commit }, sites) {
    commit("setSitesMutation", sites);
  },

  setReportSite({ commit }, site) {
    commit("setReportSiteMutation", site);
  },

  updateSiteMapDiff({ commit }, siteMap) {
    commit("siteMapDiffMutation", siteMap);
  },

  wsListenersUp({ commit }) {
    commit("wsListenersUpMutation");
  },

  setCompanyName({ commit }, companyName) {
    commit("companyNameMutation", companyName);
  },

  setCompanygst({ commit }, gst) {
    commit("gstMutation", gst);
  },

  setCompanyCurrency({ commit }, currency) {
    commit("currencyMutation", currency);
  },

  async initialSiteMap({ commit }) {
    const res = await api.get_stations();
    commit("initialSiteMapMutation", res.rows[0].value);
  },

  updateSiteMap({ commit }, siteMap) {
    commit("siteMapMutation", siteMap);
  },

  updateChannels({ commit }, channels) {
    commit("updateChannelsMutation", channels);
  },

  socketStore({ commit }, socket) {
    commit("socketStoreMutation", socket);
  },

  loading({ commit }, val) {
    commit("loading", val);
  },

  async loadCoreData({ commit, dispatch }) {
    commit("companyNameMutation", window.localStorage.getItem("company"));
    await dispatch("initialSiteMap");
    await dispatch("getSettings");
    commit("siteMapLoadedMutation");
  },

  async loadPOSData({ commit, dispatch, state }) {
    if (state.posDataLoaded) return;
    await dispatch("getPOSProducts");
    await dispatch("getPOSCategories");
    await dispatch("getModifierSets");
    await dispatch("setRegions");
    commit("posDataLoadedMutation");
  },

  async loadStripeData({ commit, dispatch, state }) {
    if (state.stripeDataLoaded) return;
    await dispatch("setBeamyProducts");
    if (state.companySettings.stripe_subscribed) {
      await dispatch("getStripeCustomerDetails");
      if (state.stripeCustomerDetails && state.stripeCustomerDetails.id) {
        await dispatch("getUpcomingStripeInvoice");
      }
    }
    commit("stripeDataLoadedMutation");
  },

  async loadCogsData({ commit, dispatch, state }) {
    if (state.cogsDataLoaded) return;
    if (!state.cogsEnabled) {
      commit("cogsDataLoadedMutation");
      return;
    }
    await dispatch("getCogsSuppliers");
    await dispatch("getCogsProducts");
    await dispatch("getCogsUOM");
    await dispatch("getCogsBaseRecipes");
    await dispatch("getCogsMenuRecipes");
    await dispatch("getCogsRecipeCategories");
    commit("cogsDataLoadedMutation");
  },

  async getAllData({ commit, dispatch }) {
    commit("resetPosDataLoadedMutation");
    commit("resetStripeDataLoadedMutation");
    commit("resetCogsDataLoadedMutation");
    await dispatch("loadCoreData");
    await dispatch("loadPOSData");
    await dispatch("loadStripeData");
    await dispatch("loadCogsData");
  },

  async getSettings({ commit, dispatch }) {
    // if (!Object.keys(state.siteSettings).length) {
    const companyString = await localStorage.getItem("company");

    // const siteString = await window.localStorage.getItem("site");
    const sites = await api.get('/api/company/get_sites');
    const companyResults = await api.get('/api/company/get_pos');
    const companySettings = await api.get('/api/company/get_settings');
    const websites = await api.get('/api/company/get_company_websites');
    const settings = await companyResults.rows[0].value.settings;
    await commit("companySettingMutation", companySettings.rows[0].value);
    await window.localStorage.setItem("sites", JSON.stringify(sites.rows[0].value.sites));
    companySettings.rows[0].value.cogs_enable ? commit("cogsEnabledMutation", true) : commit("cogsEnabledMutation", false);
    companySettings.rows[0].value.training_enable ? dispatch("handleTrainingEnabled", true) : commit("trainingEnabledMutation", false);
    companySettings.rows[0].value.timezone ? commit("timezoneMutation", companySettings.rows[0].value.timezone) : 0;
    //companySettings.rows[0].value.company_name ? commit("companyNameMutation", companySettings.rows[0].value.company_name) : 0;
    companySettings.rows[0].value.gst ? commit("gstMutation", companySettings.rows[0].value.gst) : 0;
    companySettings.rows[0].value.currency ? commit("currencyMutation", companySettings.rows[0].value.currency) : 0;
    if (Array.isArray(websites.rows[0].value.websites) && websites.rows[0].value.websites.length > 0) {
      commit("setWebsitesMutation", websites.rows[0].value.websites);
    } else {
      commit("setWebsitesMutation", []);
    }
    await commit("initialiseSiteSettingsMutation", settings);
    // }
    // await dispatch("loading");
  },

  async setWebsites({ commit }, websites) {
    await api.save_company_settings("website", websites);
    commit("setWebsitesMutation", websites);
  },

  updateSettings({ commit }, settings) {
    commit("updateSiteSettingsMutation", settings);
  },
  async getCogsSuppliers({ commit }) {
    const result = await api.get_suppliers();
    if (result) {
      await commit("cogsSuppliersMutation", result);
    }
  },

  async getCogsProducts({ commit }) {
    const result = await api.get_products();
    await commit("cogsProductsMutation", result);
  },

  async getCogsUOM({ commit }) {
    const result = await api.get_uom();
    await commit("cogsUOMMutation", result);
  },

  async getCogsBaseRecipes({ commit }) {
    const result = await api.get_base_recipes();
    await commit("cogsBaseRecipeMutation", result);
  },

  async getCogsMenuRecipes({ commit }) {
    const result = await api.get_menu_recipes();
    await commit("cogsMenuRecipeMutation", result);
  },

  async getCogsRecipeCategories({ commit }) {
    const result = await api.get_recipe_categories();
    await commit("cogsRecipeCategories", result);
  },

  async getPOSCategories({ commit }) {
    const sites = JSON.parse(localStorage.getItem("sites"));
    await Promise.all(sites.map(async (site) => {
      const result = await api.getUrl("/api/categories/get/" + state.companyName + "/" + site + "?include_docs=false");
      commit("POSCategoriesMutation", { site: site, result: result.rows });
    }));
  },

  async getPOSProducts({ commit }) {
    const sites = JSON.parse(localStorage.getItem("sites"));
    await Promise.all(sites.map(async (site) => {
      const result = await api.getUrl("/api/products/get/" + state.companyName + "/" + site + "?include_docs=false");
      commit("POSProductsMutation", { site: site, result: result.rows });
    }));
  },

  async handleTrainingEnabled({ commit, dispatch, rootGetters }, enabled) {
    commit("trainingEnabledMutation", enabled);
    if (enabled && !rootGetters["training/isLoaded"]) {
      await dispatch("training/setEmployees", await api.get_employees());
      await dispatch("training/setRoles", await api.get_roles());
      await dispatch("training/setForms", await api.get_forms());
      await dispatch("training/setLoaded", true);
    }
  },
};

const mutations = {
  updateProductMutation(state, doc) {
    let index = state.POSProducts[doc.site].findIndex((prod) => prod.id == doc._id);
    if (index == -1) {
      state.POSProducts[doc.site].push({ id: doc._id, key: doc._id, value: doc.doc });
    } else {
      state.POSProducts[doc.site][index] = { id: doc._id, key: doc._id, value: doc.doc };
    }
  },

  updateCategoryMutation(state, doc) {
    let index = state.POSCategories[doc.site].findIndex((prod) => prod.id == doc._id);
    if (index == -1) {
      state.POSCategories[doc.site].push({ id: doc._id, key: doc.doc, value: doc.doc });
    } else {
      state.POSCategories[doc.site][index] = { id: doc._id, key: doc.doc, value: doc.doc };
    }
  },

  updateCompanySettingsMutation(state, siteDoc) {
    state.siteSettings[siteDoc.site] = siteDoc.doc.settings[siteDoc.site];
  },

  setKeyValueMutation(state, key, value, site) {
    if (state.siteSettings[site]) {
      state.siteSettings[site][key] = value;
    }
  },

  setWebsitesMutation(state, websites) {
    state.websites = websites;
  },

  companySettingMutation(state, companySettings) {
    state.companySettings = companySettings;
  },

  cogsEnabledMutation(state, enabled) {
    state.cogsEnabled = enabled;
  },

  trainingEnabledMutation(state, enabled) {
    state.trainingEnabled = enabled;
  },

  timezoneMutation(state, tz) {
    state.timezone = tz;
  },

  setSitesMutation(state, sites) {
    state.sites = sites;
  },

  initialiseCategoriesMutation(state, categories) {
    state.categories = categories;
  },

  loading(state, val) {
    state.loading = val;
  },

  setReportSiteMutation(state, site) {
    state.reportSite = site;
  },

  initialSiteMapMutation(state, siteMap) {
    state.siteMap = siteMap;
  },

  siteMapLoadedMutation(state) {
    state.siteMapLoaded = true;
  },

  posDataLoadedMutation(state) { state.posDataLoaded = true; },
  stripeDataLoadedMutation(state) { state.stripeDataLoaded = true; },
  cogsDataLoadedMutation(state) { state.cogsDataLoaded = true; },
  resetPosDataLoadedMutation(state) { state.posDataLoaded = false; },
  resetStripeDataLoadedMutation(state) { state.stripeDataLoaded = false; },
  resetCogsDataLoadedMutation(state) { state.cogsDataLoaded = false; },

  companyNameMutation(state, companyName) {
    state.companyName = companyName;
  },

  gstMutation(state, gst) {
    state.gst = gst;
  },

  currencyMutation(state, currency) {
    state.currency = currency;
  },

  siteMapMutation(state, incomingSiteMap) {
    /* eslint-disable no-unused-vars */
    const [stationType, company, siteName] = incomingSiteMap.channel.topic.split(":");
    for (const siteMap of state.siteMap) {
      const stations = siteMap.stations[stationType];
      const presence = Object.keys(incomingSiteMap.presence);

      for (const station of stations) {
        if (presence.includes(station.name)) {
          station.active = true;
        }
      }
    }
  },

  siteMapDiffMutation(state, incomingSiteMap) {
    /* eslint-disable no-unused-vars */
    const [stationType, company, siteName] = incomingSiteMap.channel.topic.split(":");

    if (Object.keys(incomingSiteMap.presence.joins).length) {
      Object.keys(incomingSiteMap.presence.joins).forEach((join) => {
        if (join.split(":")[0] !== "admin") {
          state.siteMap.forEach((site) => {
            site.site == siteName
              ? site.stations[stationType].forEach((station) => {
                  if (station.name == join) {
                    station.active = true;
                  }
                })
              : null;
          });
        }
      });
    }

    if (Object.keys(incomingSiteMap.presence.leaves).length) {
      Object.keys(incomingSiteMap.presence.leaves).forEach((leave) => {
        if (leave.split(":")[0] !== "admin") {
          state.siteMap.forEach((site) => {
            site.site == siteName
              ? site.stations[stationType].forEach((station) => {
                  if (station.name == leave) {
                    station.active = false;
                  }
                })
              : null;
          });
        }
      });
    }
  },

  initialiseSiteSettingsMutation(state, settings) {
    state.siteSettings = settings;
  },

  updateSiteSettingsMutation(state, siteSettings) {
    state.siteSettings = siteSettings;
  },

  updateChannelsMutation(state, channels) {
    state.channels = channels;
  },

  socketStoreMutation(state, socket) {
    state.socket = socket;
  },

  wsListenersUpMutation(state) {
    state.wsListenersUp = !state.wsListenersUp;
  },

  cogsSuppliersMutation(state, suppliers) {
    state.cogsSuppliers = suppliers;
  },

  cogsProductsMutation(state, products) {
    state.cogsProducts = products;
  },

  cogsUOMMutation(state, uoms) {
    state.cogsUOM = uoms;
  },

  cogsBaseRecipeMutation(state, cogsBaseRecipes) {
    state.cogsBaseRecipes = cogsBaseRecipes;
  },

  cogsMenuRecipeMutation(state, cogsMenuRecipes) {
    state.cogsMenuRecipes = cogsMenuRecipes;
  },

  cogsRecipeCategories(state, cogsRecipeCategories) {
    state.cogsRecipeCategories = cogsRecipeCategories;
  },

  POSCategoriesMutation(state, f) {
    state.POSCategories[f.site] = f.result;
  },

  POSProductsMutation(state, h) {
    state.POSProducts[h.site] = h.result;
  },

  openingHoursMutation(state, hours) {
    let site = hours.site;
    state.siteSettings[site].hours = hours.hours;
  },

  regionMutation(state, r) {
    let itemToFind = r.site;
    state.region.forEach((item, index) => {
      if (item.site == itemToFind) {
        state.region.splice(index, 1);
      }
    });
    state.region.push(r);
  },

  regionsMutation(state, regions) {
    state.regions = regions;
  },

  beamyProductsMutation(state, products) {
    state.beamyProducts = products;
  },

  stripeCustomerDetailsMutation(state, details) {
    state.stripeCustomerDetails = details;
  },

  setSubscribedProductsMutation(state, products) {
    state.subscribedProducts = products;
    // state.loading = false;
  },

  setSocketConnectedMutation(state, value) {
    state.socketConnected = value;
  },

  updateModifiersMutation(state, doc) {
    state.modifiers[doc.site] = doc.doc;
  },

  setModifiersMutation(state, doc) {
    let index = state.modifiers[doc.site].findIndex((prod) => prod.id == doc._id);
    if (index == -1) {
      state.modifiers[doc.site].push({ id: doc._id, key: doc._id, value: doc.doc });
    } else {
      state.modifiers[doc.site][index] = { id: doc._id, key: doc._id, value: doc.doc };
    }
  },

  setStripeUpcomingInvoiceMutation(state, invoice) {
    state.stripeUpcomingInvoice = invoice;
  },

  cogsSelectedSiteMutation(state, site) {
    state.cogsSelectedSite = site;
  },
};

const stateModule = {
  state,
  strict: true,
  getters,
  actions,
  mutations,
};

export default stateModule;
